import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  Award, 
  Calendar, 
  ThumbsUp, 
  ThumbsDown,
  Shield,
  Clock,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const certificationLevels = {
  standard: { label: 'Standard', color: 'bg-gray-500' },
  advanced: { label: 'Advanced', color: 'bg-blue-500' },
  professional: { label: 'Professional', color: 'bg-emerald-500' }
};

export default function RefereeProfilePage() {
  const { refereeId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [referee, setReferee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [votingStatus, setVotingStatus] = useState({});

  useEffect(() => {
    fetchRefereeProfile();
  }, [refereeId]);

  const fetchRefereeProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/api/referee/profile/${refereeId}`);
      if (response.ok) {
        const data = await response.json();
        setReferee(data);
        
        // Check voting status for each match
        if (token && data.recent_matches) {
          for (const match of data.recent_matches) {
            checkCanVote(match.match_id);
          }
        }
      } else {
        toast.error('Referee not found');
        navigate(-1);
      }
    } catch (error) {
      console.error('Failed to fetch referee:', error);
      toast.error('Failed to load referee profile');
    } finally {
      setLoading(false);
    }
  };

  const checkCanVote = async (matchId) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/referee/${refereeId}/can-vote/${matchId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVotingStatus(prev => ({ ...prev, [matchId]: data }));
      }
    } catch (error) {
      console.error('Failed to check voting status:', error);
    }
  };

  const handleVote = async (matchId, vote) => {
    if (!token) {
      toast.error('Please login to vote');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/referee/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          match_id: matchId,
          referee_id: refereeId,
          vote: vote
        })
      });
      
      if (response.ok) {
        toast.success('Vote recorded!');
        setVotingStatus(prev => ({ 
          ...prev, 
          [matchId]: { can_vote: false, reason: 'Already voted', your_vote: vote }
        }));
        fetchRefereeProfile(); // Refresh to update rating
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to vote');
      }
    } catch (error) {
      toast.error('Failed to submit vote');
    }
  };

  const getExperienceText = (experience) => {
    if (!experience) return 'New';
    const { years, months } = experience;
    if (years === 0 && months === 0) return 'New to the league';
    if (years === 0) return `${months} month${months !== 1 ? 's' : ''} of experience`;
    if (months === 0) return `${years} year${years !== 1 ? 's' : ''} of experience`;
    return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''} of experience`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="text-emerald-500">Loading referee profile...</div>
      </div>
    );
  }

  if (!referee) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400">Referee not found</p>
          <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
        </div>
      </div>
    );
  }

  const cert = certificationLevels[referee.certification_level] || certificationLevels.standard;

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#1a1a1b]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft size={20} />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="text-emerald-400" size={24} />
            <span className="font-heading text-lg text-white uppercase">Match Official</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Profile Card */}
        <Card className="bg-[#1a1a1b] border-white/10 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              {/* Photo */}
              {referee.photo ? (
                <img 
                  src={referee.photo}
                  alt={referee.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-emerald-500/30"
                />
              ) : (
                <div className="w-32 h-32 bg-emerald-500/20 rounded-full flex items-center justify-center border-4 border-emerald-500/30">
                  <span className="text-emerald-400 text-4xl font-bold">
                    {referee.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              
              {/* Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-2xl font-heading font-bold text-white uppercase mb-2">
                  {referee.name}
                </h1>
                
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${cert.color} text-white`}>
                    {cert.label}
                  </span>
                  {referee.organization_name && (
                    <span className="text-gray-400 text-sm">
                      {referee.organization_name}
                    </span>
                  )}
                </div>
                
                {/* Stats Row */}
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-sm">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Clock size={16} className="text-emerald-400" />
                    <span>{getExperienceText(referee.experience)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Award size={16} className="text-emerald-400" />
                    <span>{referee.matches_officiated || 0} matches officiated</span>
                  </div>
                </div>
                
                {/* Rating */}
                {referee.rating && referee.rating.total_votes > 0 && (
                  <div className="mt-4 p-3 bg-white/5 rounded-lg inline-flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <ThumbsUp className="text-green-400" size={18} />
                      <span className="text-green-400 font-medium">{referee.rating.thumbs_up}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ThumbsDown className="text-red-400" size={18} />
                      <span className="text-red-400 font-medium">{referee.rating.thumbs_down}</span>
                    </div>
                    <span className={`text-lg font-bold ${
                      referee.rating.percentage >= 70 ? 'text-green-400' : 
                      referee.rating.percentage >= 50 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {referee.rating.percentage}% positive
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Bio */}
            {referee.bio && (
              <div className="mt-6 p-4 bg-white/5 rounded-lg">
                <h3 className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <User size={14} />
                  Apart from match officiating
                </h3>
                <p className="text-white italic">"{referee.bio}"</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Matches */}
        {referee.recent_matches && referee.recent_matches.length > 0 && (
          <Card className="bg-[#1a1a1b] border-white/10">
            <CardContent className="p-6">
              <h2 className="font-heading text-lg font-bold text-white uppercase mb-4 flex items-center gap-2">
                <Calendar className="text-emerald-400" size={20} />
                Recent Matches Officiated
              </h2>
              
              <div className="space-y-3">
                {referee.recent_matches.map((match) => {
                  const voteStatus = votingStatus[match.match_id];
                  return (
                    <div 
                      key={match.match_id}
                      className="p-4 bg-white/5 rounded-lg"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <p className="text-white font-medium">
                            {match.home_team_name} vs {match.away_team_name}
                          </p>
                          <p className="text-gray-400 text-sm">
                            {match.match_date}
                            {match.status === 'completed' && (
                              <span className="ml-2 text-emerald-400">
                                Final: {match.home_score} - {match.away_score}
                              </span>
                            )}
                          </p>
                        </div>
                        
                        {/* Voting UI */}
                        {match.status === 'completed' && (
                          <div className="flex items-center gap-2">
                            {voteStatus?.can_vote ? (
                              <>
                                <span className="text-xs text-gray-400 mr-2">Rate this official:</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleVote(match.match_id, 'up')}
                                  className="text-gray-400 hover:text-green-400 hover:bg-green-500/10"
                                >
                                  <ThumbsUp size={18} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleVote(match.match_id, 'down')}
                                  className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                >
                                  <ThumbsDown size={18} />
                                </Button>
                              </>
                            ) : voteStatus?.your_vote ? (
                              <span className="flex items-center gap-2 text-sm">
                                <span className="text-gray-400">Your vote:</span>
                                {voteStatus.your_vote === 'up' ? (
                                  <ThumbsUp size={16} className="text-green-400" />
                                ) : (
                                  <ThumbsDown size={16} className="text-red-400" />
                                )}
                              </span>
                            ) : voteStatus?.reason ? (
                              <span className="text-xs text-gray-500">{voteStatus.reason}</span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
